import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@devhop/ui";
import { RiCheckLine, RiErrorWarningLine } from "@remixicon/react";
import { useEffect, useState } from "react";
import type {
  FeedCommentRow,
  FeedSessionProductRow,
} from "@/modules/cf-feed/presentation/api/types";
import { AppImage } from "@/shared/ui/AppImage";

type ManualOrderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  comments: FeedCommentRow[];
  commentCount: number;
  products: FeedSessionProductRow[];
  productsLoading?: boolean;
  productsTotal: number;
  productsOffset: number;
  productsLimit: number;
  productSearch: string;
  onProductSearchChange: (value: string) => void;
  onProductsPageChange: (offset: number) => void;
  errorMessage?: string | null;
  onClearError?: () => void;
  onSubmit: (data: {
    cfCode: string;
    quantity: number;
    specialWorkUnits?: Array<{
      unitIndex: number;
      name?: string | null;
      number?: string | null;
    }>;
  }) => void;
  loading?: boolean;
};

function availableLabel(product: FeedSessionProductRow): string {
  if (product.availableQuantity == null) return "ບໍ່ຈຳກັດ";
  if (product.availableQuantity <= 0) return "ໝົດແລ້ວ";
  return `${product.availableQuantity} ຊິ້ນ`;
}

export function ManualOrderDialog({
  open,
  onOpenChange,
  comments,
  commentCount,
  products,
  productsLoading,
  productsTotal,
  productsOffset,
  productsLimit,
  productSearch,
  onProductSearchChange,
  onProductsPageChange,
  errorMessage,
  onClearError,
  onSubmit,
  loading,
}: ManualOrderDialogProps) {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [quantity, setQuantity] = useState("1");
  const [specialValues, setSpecialValues] = useState<
    Record<number, { name: string; number: string }>
  >({});

  useEffect(() => {
    if (!open) return;
    setSelectedProductId(null);
    setQuantity("1");
    setSpecialValues({});
  }, [open]);

  const selectedProduct = products.find(
    (product) => product.id === selectedProductId,
  );
  const parsedQuantity = Number(quantity);
  const validQuantity =
    Number.isInteger(parsedQuantity) &&
    parsedQuantity >= 1 &&
    (selectedProduct?.availableQuantity == null ||
      parsedQuantity <= selectedProduct.availableQuantity);
  const requiresName = Boolean(
    selectedProduct?.options.some(
      (option) => option.requiresCustomerInput && option.code === "NAME_PRINT",
    ),
  );
  const requiresNumber = Boolean(
    selectedProduct?.options.some(
      (option) =>
        option.requiresCustomerInput && option.code === "NUMBER_PRINT",
    ),
  );
  const specialUnitIndexes =
    validQuantity && (requiresName || requiresNumber)
      ? Array.from(
          { length: Math.min(parsedQuantity, 100) },
          (_, index) => index,
        )
      : [];
  const rangeStart = productsTotal > 0 ? productsOffset + 1 : 0;
  const rangeEnd = Math.min(productsOffset + products.length, productsTotal);
  const hasPreviousPage = productsOffset > 0;
  const hasNextPage = productsOffset + productsLimit < productsTotal;

  const handleOpen = (next: boolean) => {
    if (!next) onClearError?.();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>ສ້າງອໍເດີ້ (ແອດມິນ)</DialogTitle>
        </DialogHeader>

        {commentCount > 1 ? (
          <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
            ສ້າງອໍເດີ້ໃຫ້ <strong>{commentCount}</strong> ຄອມເມັນ
          </div>
        ) : comments[0] ? (
          <p className="text-muted-foreground text-sm">
            {comments[0].commenterName}: {comments[0].commentText.slice(0, 120)}
          </p>
        ) : null}

        {errorMessage ? (
          <div
            role="alert"
            className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive text-sm"
          >
            <RiErrorWarningLine className="mt-0.5 size-4 shrink-0" />
            <p className="min-w-0 leading-relaxed">{errorMessage}</p>
          </div>
        ) : null}

        <div className="space-y-2 py-1">
          <div className="flex items-end justify-between gap-3">
            <div>
              <Label>ເລືອກສິນຄ້າ</Label>
              <p className="mt-0.5 text-muted-foreground text-xs">
                ສະແດງສະເພາະສິນຄ້າທີ່ເປີດຂາຍໃນໂພສນີ້
              </p>
            </div>
            <span className="shrink-0 text-muted-foreground text-xs">
              {rangeStart}-{rangeEnd} / {productsTotal}
            </span>
          </div>

          <Input
            type="search"
            aria-label="ຄົ້ນຫາສິນຄ້າ"
            placeholder="ຄົ້ນຫາຊື່, SKU ຫຼື ລະຫັດ CF…"
            value={productSearch}
            disabled={loading}
            onChange={(event) => {
              setSelectedProductId(null);
              onProductSearchChange(event.target.value);
            }}
          />

          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {productsLoading ? (
              <div className="rounded-lg border border-dashed px-3 py-8 text-center text-muted-foreground text-sm">
                ກຳລັງໂຫຼດສິນຄ້າ...
              </div>
            ) : products.length === 0 ? (
              <div className="rounded-lg border border-dashed px-3 py-8 text-center text-muted-foreground text-sm">
                {productSearch.trim() ? "ບໍ່ພົບສິນຄ້າຕາມຄຳຄົ້ນຫາ" : "ບໍ່ມີສິນຄ້າທີ່ເປີດຂາຍ"}
              </div>
            ) : (
              products.map((product) => {
                const selected = selectedProductId === product.id;
                const unavailable = product.availableQuantity === 0;
                return (
                  <Button
                    key={product.id}
                    type="button"
                    variant="outline"
                    disabled={unavailable || loading}
                    aria-pressed={selected}
                    className={`h-auto w-full justify-start gap-3 whitespace-normal p-3 text-left ${
                      selected
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : ""
                    }`}
                    onClick={() => {
                      onClearError?.();
                      setSelectedProductId(product.id);
                      setQuantity("1");
                      setSpecialValues({});
                    }}
                  >
                    <span className="size-11 shrink-0 overflow-hidden rounded-md border bg-muted">
                      <AppImage
                        src={product.imageUrl}
                        alt={product.title}
                        className="size-full object-cover"
                        showLoading={false}
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {product.title}
                      </span>
                      <span className="mt-0.5 block text-muted-foreground text-xs">
                        CF {product.cfCode}
                      </span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                      {product.isPreorder ? (
                        <Badge variant="secondary">ພຣີອໍເດີ</Badge>
                      ) : null}
                      <Badge variant={unavailable ? "destructive" : "outline"}>
                        ເຫຼືອ {availableLabel(product)}
                      </Badge>
                      {selected ? (
                        <RiCheckLine className="size-5 text-primary" />
                      ) : null}
                    </span>
                  </Button>
                );
              })
            )}
          </div>

          {productsTotal > productsLimit ? (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!hasPreviousPage || productsLoading}
                onClick={() => {
                  setSelectedProductId(null);
                  onProductsPageChange(
                    Math.max(0, productsOffset - productsLimit),
                  );
                }}
              >
                ກ່ອນໜ້າ
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!hasNextPage || productsLoading}
                onClick={() => {
                  setSelectedProductId(null);
                  onProductsPageChange(productsOffset + productsLimit);
                }}
              >
                ຖັດໄປ
              </Button>
            </div>
          ) : null}
        </div>

        <div className="space-y-1">
          <Label htmlFor="manual-order-qty">ຈຳນວນຕໍ່ອໍເດີ້</Label>
          <Input
            id="manual-order-qty"
            type="number"
            min={1}
            max={selectedProduct?.availableQuantity ?? undefined}
            value={quantity}
            disabled={!selectedProduct || loading}
            onChange={(event) => {
              onClearError?.();
              setQuantity(event.target.value);
            }}
          />
          {selectedProduct?.availableQuantity != null && !validQuantity ? (
            <p className="text-destructive text-xs">
              ຈຳນວນຕ້ອງຢູ່ລະຫວ່າງ 1 ຫາ {selectedProduct.availableQuantity}
            </p>
          ) : null}
        </div>

        {specialUnitIndexes.length > 0 ? (
          <div className="space-y-3 rounded-lg border p-3">
            <div>
              <Label>ຂໍ້ມູນງານພິມພິເສດ</Label>
              <p className="mt-0.5 text-muted-foreground text-xs">
                ສາມາດສ້າງອໍເດີ້ໄວ້ກ່ອນ ແລ້ວຕື່ມຂໍ້ມູນພາຍຫຼັງໄດ້
              </p>
              {commentCount > 1 ? (
                <p className="mt-1 text-amber-600 text-xs dark:text-amber-400">
                  ຂໍ້ມູນນີ້ຈະໃຊ້ກັບທຸກອໍເດີ້ທີ່ເລືອກ
                </p>
              ) : null}
            </div>
            <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {specialUnitIndexes.map((unitIndex) => {
                const values = specialValues[unitIndex] ?? {
                  name: "",
                  number: "",
                };
                return (
                  <div
                    key={unitIndex}
                    className="grid gap-2 rounded-md bg-muted/40 p-3 sm:grid-cols-2"
                  >
                    <p className="font-medium text-xs sm:col-span-2">
                      ຊິ້ນທີ {unitIndex + 1}
                    </p>
                    {requiresName ? (
                      <div className="space-y-1">
                        <Label htmlFor={`manual-order-name-${unitIndex}`}>
                          ຊື່ທີ່ຈະພິມ
                        </Label>
                        <Input
                          id={`manual-order-name-${unitIndex}`}
                          maxLength={100}
                          value={values.name}
                          disabled={loading}
                          onChange={(event) =>
                            setSpecialValues((current) => ({
                              ...current,
                              [unitIndex]: {
                                ...(current[unitIndex] ?? {
                                  name: "",
                                  number: "",
                                }),
                                name: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ) : null}
                    {requiresNumber ? (
                      <div className="space-y-1">
                        <Label htmlFor={`manual-order-number-${unitIndex}`}>
                          ເບີທີ່ຈະພິມ
                        </Label>
                        <Input
                          id={`manual-order-number-${unitIndex}`}
                          maxLength={20}
                          value={values.number}
                          disabled={loading}
                          onChange={(event) =>
                            setSpecialValues((current) => ({
                              ...current,
                              [unitIndex]: {
                                ...(current[unitIndex] ?? {
                                  name: "",
                                  number: "",
                                }),
                                number: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {parsedQuantity > specialUnitIndexes.length ? (
              <p className="text-muted-foreground text-xs">
                ອີກ {parsedQuantity - specialUnitIndexes.length} ຊິ້ນ
                ສາມາດຕື່ມຂໍ້ມູນໄດ້ພາຍຫຼັງ
              </p>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={loading || !selectedProduct || !validQuantity}
            onClick={() => {
              if (!selectedProduct || !validQuantity) return;
              onSubmit({
                cfCode: selectedProduct.cfCode,
                quantity: parsedQuantity,
                specialWorkUnits: specialUnitIndexes.flatMap((unitIndex) => {
                  const values = specialValues[unitIndex];
                  const name = values?.name.trim() ?? "";
                  const number = values?.number.trim() ?? "";
                  if (!name && !number) return [];
                  return [
                    {
                      unitIndex,
                      name: name || null,
                      number: number || null,
                    },
                  ];
                }),
              });
            }}
          >
            {loading
              ? "ກຳລັງບັນທຶກ..."
              : commentCount > 0
                ? `ບັນທຶກ ${commentCount} ອໍເດີ້`
                : "ບັນທຶກອໍເດີ້"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
